import React, { useCallback } from "react";
import { Link, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useAuth0 } from "@auth0/auth0-react";
import { stopCrossAppImpersonation } from "@/api/impersonation/impersonationApi";
import { useAppSelector } from "@/app/hooks";
import logoOnNavy from "@/assets/images/logo-main-on-navy.svg";
import { useIsImpersonating } from "@/hooks/useIsImpersonating";
import { isPreEpCompany, isPreEpMember } from "../../config/preEpStage";
import { PreEpGlows } from "../PreEpUi/PreEpUi";

/**
 * Full-screen frame for the Pre-EP pages: navy, a slim bar with the logo
 * and the user menu (Sign out only), no sidebar or app header. A company
 * that isn't Pre-EP is sent back to the regular home page.
 */
const PreEpShell: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const settings = useAppSelector(
    (state) => state.userAppSettings.userAppSettings,
  );
  const isImpersonating = useIsImpersonating();

  const exitImpersonation = useCallback(async () => {
    await stopCrossAppImpersonation();
    await getAccessTokenSilently({ cacheMode: "off" });
    localStorage.removeItem("selectedCompanyUuid");
    window.location.href = "/";
  }, [getAccessTokenSilently]);

  if (settings && !isPreEpCompany(settings)) {
    return <Navigate replace to="/hub" />;
  }

  const fullName = settings ? `${settings.firstName} ${settings.lastName}` : "";
  const initials = fullName
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("");
  const menuItem =
    "block w-full px-3 py-1.5 text-left text-sm text-[#E6EAF5] data-[focus]:bg-[#1B274B]";

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0A1430] text-white">
      <PreEpGlows />
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-[#1E2A4F] bg-[#0D1836]/90 px-4 backdrop-blur-sm sm:px-6">
        <Link
          aria-label="Your Pre-EP steps"
          to={isPreEpMember(settings) ? "/pre-ep" : "/hub"}
        >
          <img
            alt="CertainPath"
            className="block h-6 w-auto sm:h-7"
            src={logoOnNavy}
          />
        </Link>
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center gap-2.5 rounded-full p-1 text-sm font-semibold text-white hover:bg-white/5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2A3762] text-sm font-bold">
              {initials}
            </span>
            <span className="hidden sm:inline">{fullName}</span>
          </MenuButton>
          <MenuItems className="absolute right-0 z-30 mt-2 w-44 rounded-lg border border-[#2C3A66] bg-[#111D40] py-1 shadow-lg focus:outline-none">
            <MenuItem>
              <button
                className={menuItem}
                onClick={() => navigate("/logout")}
                type="button"
              >
                Sign out
              </button>
            </MenuItem>
            {isImpersonating && (
              <MenuItem>
                <button
                  className={menuItem}
                  onClick={() => void exitImpersonation()}
                  type="button"
                >
                  Exit impersonation
                </button>
              </MenuItem>
            )}
          </MenuItems>
        </Menu>
      </header>
      <main className="relative">
        <Outlet />
      </main>
    </div>
  );
};

export default PreEpShell;
